import type { FilterSchema } from '@/shared/components/filter/validation'
import type {
	OptimizationIDModel,
	ProductsByEngineModel,
	StatisticsInterpretabilityGroupedResponseModel,
	StatisticsInterpretabilityResponse,
} from '@/shared/generated'

import { any, getOr, map, prop } from 'lodash/fp'
import { useTranslation } from 'react-i18next'

import { useProductCountByEngineStatisticsQuery } from '@/entities/bi'
import {
	getFinishedAndLockedOptimizations,
	useOptimizationsQuery,
} from '@/entities/optimization'
import { useInterpretabilityStatisticQuery } from '@/entities/optimization-statistic'

import { Operators } from '@/shared/components/query-builder'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'

import PercentDifferenceBadge from '../../PercentDifferenceBadge'
import { Widget, WidgetContent, WidgetHeader } from '../../Widget'
import WidgetErrorState from '../../WidgetErrorState'

export default function NumericSalesUnitsImpactWidget({
	commonFilters,
	className,
}: {
	className?: string
	commonFilters: FilterSchema[]
}) {
	const { t } = useTranslation()

	const optimizationsQuery = useOptimizationsQuery({
		select: getFinishedAndLockedOptimizations,
	})
	const productCountQuery = useProductCountByEngineStatisticsQuery(
		{
			filters: [
				...commonFilters,
				{
					name: 'optimization_group_id',
					operation: Operators.IN,
					value: map(
						prop('optimization_group_id'),
						optimizationsQuery.data ?? []
					),
				},
			],
		},
		{
			enabled: !!optimizationsQuery.data,
			select: computeProductsCount,
		}
	)
	const salesUnitsQuery = useInterpretabilityStatisticQuery(
		{
			optimizations: optimizationsQuery.data as OptimizationIDModel[],
			filters: commonFilters,
		},
		{
			enabled: !!optimizationsQuery.data,
			select: calculateSalesUnitsStatistics,
		}
	)
	const isLoading = any(prop('isLoading'), [
		salesUnitsQuery,
		productCountQuery,
		optimizationsQuery,
	])
	const isError = any(prop('isError'), [
		salesUnitsQuery,
		productCountQuery,
		optimizationsQuery,
	])

	return (
		<Widget className={cn('relative truncate', className)}>
			<WidgetHeader
				title={t('widget.sales_units_impact.title', {
					defaultValue: 'Sales Units Impact',
				})}
				tooltip={t('widget.sales_units_impact.tooltip', {
					defaultValue:
						'Projected sales units over the next 7 days based on recommended prices. The % delta shows the uplift versus keeping current prices unchanged.',
				})}
				subtitle={t('widget.sales_units_impact.subtitle', {
					defaultValue: 'Projected Sales Units Uplift',
				})}
			/>
			<WidgetErrorState isTinted={isError} size='small' />
			<WidgetContent className='truncate'>
				<div className='w-full'>
					{isLoading && <Loader />}
					{!isLoading && (
						<div className='space-y-1'>
							<PercentDifferenceBadge
								value={salesUnitsQuery?.data?.salesUnitsDeltaPercent ?? 0}
							/>
							<p className='truncate text-3xl font-semibold'>
								{formatNumber(
									salesUnitsQuery?.data?.salesUnits ?? 0,
									NumberFormats.Compact
								)}
							</p>
						</div>
					)}
				</div>
			</WidgetContent>
		</Widget>
	)
}
const Loader = () => (
	<div className='space-y-1'>
		<Skeleton className='col-span-2 h-4 w-32' />
		<Skeleton className='col-span-2 h-4 w-32' />
		<Skeleton className='col-span-2 h-8 w-32' />
	</div>
)

const calculateSalesUnitsStatistics = (
	data:
		| StatisticsInterpretabilityResponse
		| StatisticsInterpretabilityGroupedResponseModel[]
		| null
) => {
	if (!data) return { salesUnits: 0, salesUnitsDeltaPercent: 0 }

	const finalSalesUnits = getOr(
		0,
		['metrics', 'predict_sales_items.final'],
		data
	)
	const initialSalesUnits = getOr(
		0,
		['metrics', 'predict_sales_items.init'],
		data
	)
	const salesUnitsDeltaPercent =
		((finalSalesUnits - initialSalesUnits) / initialSalesUnits) * 100

	return {
		salesUnits: finalSalesUnits,
		salesUnitsDeltaPercent,
	}
}
const computeProductsCount = (data: ProductsByEngineModel[] | null) =>
	data
		?.filter(
			({ pricing_campaign_engine }) => pricing_campaign_engine !== 'locked'
		)
		.map(({ product_count }) => product_count ?? 0)
		.reduce((acc, curr) => acc + curr, 0)
